import React, { useState, useEffect, useMemo } from "react";
import "./ApplyLeave.css";
import { getGender } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";

const ApplyLeaveForm = ({ isProbation }) => {
  /* ================= STATE ================= */

  const [formData, setFormData] = useState({
    subject: "",
    startDate: "",
    endDate: "",
    leaveType: "",
    reason: "",
  });

  const [leaveSummary, setLeaveSummary] = useState(null);
  const [specialLeaves, setSpecialLeaves] = useState({});
  const [resignationApproved, setResignationApproved] = useState(false);

  const [leaveNotice, setLeaveNotice] = useState("");
  const [canSubmit, setCanSubmit] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  /* ================= DATE HELPERS ================= */

  const todayDate = new Date();
  const today = todayDate.toISOString().split("T")[0];

  const pastOneMonthDate = new Date();
  pastOneMonthDate.setMonth(pastOneMonthDate.getMonth() - 1);
  const pastOneMonth = pastOneMonthDate.toISOString().split("T")[0];

  const gender = getGender();

  /* ================= POPUP ================= */

  const showPopup = (msg) => {
    setModalMessage(msg);
    setShowModal(true);
  };

  /* ================= FETCH ================= */

  const fetchLeaveSummary = async () => {
    const data = await apiClient("/leaves/summary/");
    setLeaveSummary(data);
  };

  const fetchSpecialSummary = async () => {
    const data = await apiClient("/leaves/special_summary/");
    setSpecialLeaves(data || {});
  };

  const fetchResignationStatus = async () => {
    const data = await apiClient("/resignation/approved/");
    const approved = Boolean(data?.approved);
    setResignationApproved(approved);

    if (approved) {
      showPopup("Resignation approved. Only Loss Of Pay is allowed.");
    }
  };

  useEffect(() => {
    Promise.all([
      fetchLeaveSummary(),
      fetchSpecialSummary(),
      fetchResignationStatus(),
    ]).catch(console.error);
  }, []);

  /* ================= DERIVED VALUES ================= */

  const leaveDays = useMemo(() => {
    const { startDate, endDate, leaveType } = formData;

    if (leaveType === "Half Day") return 0.5;
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) return 0;

    return (end - start) / (1000 * 60 * 60 * 24) + 1;
  }, [formData]);

  const elAvailable = useMemo(() => {
    if (!leaveSummary?.breakdown) return 0;

    return leaveSummary.breakdown.reduce((total, b) => {
      const [used, base] = b.earned_leave.split(" / ").map(Number);
      return total + (base - used);
    }, 0);
  }, [leaveSummary]);

  const isSpecialLeaveUsed = (type) =>
    Boolean(specialLeaves?.[type]?.applied_once);

  /* ================= VALIDATION ================= */

  useEffect(() => {
    if (showModal) return;
    if (!leaveSummary) return;
    if (!formData.leaveType || !formData.startDate) {
      setLeaveNotice("");
      setCanSubmit(false);
      return;
    }

    const start = new Date(formData.startDate);
    const pastLimit = new Date(pastOneMonth);

    // 🔒 Past leave limit (1 month)
    if (start < pastLimit) {
      setLeaveNotice("You can apply leave only up to the past 1 month.");
      setCanSubmit(false);
      return;
    }

    if (formData.endDate) {
      const end = new Date(formData.endDate);

      if (end < start) {
        setLeaveNotice("End date cannot be before start date.");
        setCanSubmit(false);
        return;
      }

      if (end < pastLimit) {
        setLeaveNotice("End date cannot be older than past 1 month.");
        setCanSubmit(false);
        return;
      }
    }

    let notice = "";
    let submit = false;

    if (resignationApproved && formData.leaveType !== "Loss Of Pay") {
      notice = "Resignation approved. Only Loss Of Pay is allowed.";
    } else {
      const SPECIAL_LIMITS = {
        "Wedding Leave": 14,
        "Paternity Leave": 7,
        "Maternity Leave": 180,
      };

      const type = formData.leaveType;

      if (SPECIAL_LIMITS[type]) {
        const limit = SPECIAL_LIMITS[type];

        if (isSpecialLeaveUsed(type)) {
          notice = `${type} can be applied only once.`;
        } else if (leaveDays > limit) {
          notice = `${type} allows maximum ${limit} days.`;
        } else {
          notice = `${type} within allowed limit (${limit} days).`;
          submit = true;
        }
      } else if (type === "Loss Of Pay") {
        submit = true;
      } else if (type === "Half Day") {
        notice =
          "0.5 day will be deducted based on availability (CL → SL → EL → LOP)";
        submit = true;
      } else if (type === "Earned Leave") {
        notice =
          leaveDays > elAvailable
            ? `Only ${elAvailable} EL available`
            : `${elAvailable} EL available`;
        submit = leaveDays <= elAvailable;
      } else {
  // ✅ USE YEARLY TOTAL INSTEAD OF HALF-WISE
        const yearlyMap = {
          "Casual Leave": "casual_leave",
          "Sick Leave": "sick_leave",
        };

        const yearlyValue = leaveSummary.total?.[yearlyMap[type]];

        let remaining = 0;

        if (yearlyValue) {
          const [used, total] = yearlyValue.split(" / ").map(Number);
          remaining = Math.max(0, total - used);
        }

        notice = `${remaining} ${type} remaining`;
        submit = leaveDays <= remaining;
      }

    }

    setLeaveNotice(notice);
    setCanSubmit(submit);
  }, [
    formData,
    leaveDays,
    leaveSummary,
    resignationApproved,
    elAvailable,
    specialLeaves,
    showModal,
    pastOneMonth,
  ]);

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleCancel = () => {
    setFormData({
      subject: "",
      startDate: "",
      endDate: "",
      leaveType: "",
      reason: "",
    });
    setLeaveNotice("");
    setCanSubmit(false);
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (resignationApproved && formData.leaveType !== "Loss Of Pay") {
      showPopup("Resignation approved. Only Loss Of Pay is allowed.");
      return;
    }

    const payload = {
      subject: formData.subject.trim(),
      start_date: formData.startDate,
      end_date:
        formData.leaveType === "Half Day"
          ? formData.startDate
          : formData.endDate,
      leave_type:
        formData.leaveType === "Half Day"
          ? "Loss Of Pay"       // placeholder, backend resolves
          : formData.leaveType,
      reason: formData.reason.trim(),
      is_half_day: formData.leaveType === "Half Day",
    };

    try {
      await apiClient("/leaves/", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      handleCancel();
      showPopup("✅ Leave request submitted successfully.");

      setTimeout(() => {
        fetchLeaveSummary();
        fetchSpecialSummary();
      }, 300);
    } catch (err) {
      let message = "❌ Leave submission failed.";
      if (Array.isArray(err) && err.length) message = err[0];
      else if (err?.non_field_errors?.length)
        message = err.non_field_errors[0];
      else if (err?.detail) message = err.detail;
      else if (err?.error) message = err.error;

      showPopup(message);
    }
  };

  /* ================= OPTIONS ================= */

  const leaveOptions = [
    "Sick Leave",
    "Casual Leave",
    "Half Day",
    "Loss Of Pay",
    "Earned Leave",
    ...(gender === "Male" ? ["Paternity Leave"] : []),
    ...(gender === "Female" ? ["Maternity Leave"] : []),
    "Wedding Leave",
  ];

  const filteredLeaveOptions = resignationApproved
    ? ["Loss Of Pay"]
    : isProbation
    ? leaveOptions.filter((l) =>
        ["Sick Leave", "Casual Leave", "Half Day"].includes(l)
      )
    : leaveOptions;

  /* ================= UI ================= */

  return (
    <div className="apply-leave-form">
      <h2>Apply Leave</h2>

      <form onSubmit={handleSubmit}>
        <input
          name="subject"
          placeholder="Subject"
          value={formData.subject}
          onChange={handleChange}
          required
        />

        <input
          type="date"
          name="startDate"
          min={pastOneMonth}
          value={formData.startDate}
          onChange={handleChange}
          required
        />

        {formData.leaveType !== "Half Day" && (
          <input
            type="date"
            name="endDate"
            min={formData.startDate || pastOneMonth}
            value={formData.endDate}
            onChange={handleChange}
            required
          />
        )}

        <select
          name="leaveType"
          value={formData.leaveType}
          onChange={handleChange}
          required
        >
          <option value="">Select Leave Type</option>
          {filteredLeaveOptions.map((l) => (
            <option key={l} value={l} disabled={isSpecialLeaveUsed(l)}>
              {l}
            </option>
          ))}
        </select>

        {leaveNotice && <p className="leave-notice">{leaveNotice}</p>}

        <div className="form-actions">
          <button type="submit" disabled={!canSubmit || showModal}>
            Submit
          </button>
          <button type="button" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </form>

      {showModal && (
        <div className="modal17-overlay">
          <div className="modal17-content">
            <h3>{modalMessage}</h3>
            <button onClick={() => setShowModal(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplyLeaveForm;
